import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { reportCommunityPost } from '@/service/api/community';

export const REPORT_REASONS = [
  { code: 'SPAM', label: '垃圾广告 / 恶意营销', desc: '含欺诈、广告推广、刷屏等垃圾内容' },
  { code: 'VIOLENCE', label: '违法违规 / 涉暴涉政', desc: '违反法律法规、传播暴力血腥或政治敏感' },
  { code: 'HARASSMENT', label: '人身攻击 / 辱骂骚扰', desc: '存在谩骂、诽谤、引战或侵犯个人隐私' },
  { code: 'COPYRIGHT', label: '侵权盗用 / 未授权', desc: '侵犯他人著作权、商标权或知识产权' },
  { code: 'OTHER', label: '其他原因', desc: '其他违反社区规范或不良信息' },
];

interface ReportModalProps {
  open: boolean;
  postId?: string;
  postTitle?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ReportModal({
  open,
  postId,
  postTitle,
  onClose,
  onSuccess,
}: ReportModalProps) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [reasonCode, setReasonCode] = useState<string>('SPAM');
  const [description, setDescription] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReasonCode('SPAM');
      setDescription('');
      setErrorMsg(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      onClose();
      navigate('/auth');
      return;
    }

    if (!postId) return;

    setSubmitting(true);
    setErrorMsg(null);

    try {
      await reportCommunityPost(postId, {
        reason_code: reasonCode,
        description: description.trim() || undefined,
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('提交举报失败:', err);
      setErrorMsg(err instanceof Error ? err.message : '举报提交失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-xl overflow-hidden border border-background-200 flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/80 bg-gray-50/50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center">
              <i className="ri-flag-line text-lg"></i>
            </div>
            <div>
              <h3 className="font-semibold text-foreground-950 text-base">举报动态</h3>
              {postTitle && (
                <p className="text-xs text-foreground-400 truncate max-w-[280px]">
                  {postTitle}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="w-8 h-8 rounded-full flex items-center justify-center text-foreground-400 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5 flex-1">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200/80 text-rose-700 text-xs flex items-center gap-2">
              <i className="ri-error-warning-line text-base flex-shrink-0"></i>
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Reason Selection */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-2.5">
              请选择举报原因 <span className="text-rose-500">*</span>
            </label>
            <div className="space-y-2">
              {REPORT_REASONS.map((item) => {
                const checked = reasonCode === item.code;
                return (
                  <div
                    key={item.code}
                    onClick={() => setReasonCode(item.code)}
                    className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                      checked
                        ? 'border-rose-500 bg-rose-50/30 shadow-xs'
                        : 'border-background-200/80 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="pt-0.5">
                      <input
                        type="radio"
                        name="report_reason"
                        value={item.code}
                        checked={checked}
                        onChange={() => setReasonCode(item.code)}
                        className="w-4 h-4 text-rose-600 focus:ring-rose-500 cursor-pointer"
                      />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground-900">{item.label}</div>
                      <div className="text-xs text-foreground-400 mt-0.5">{item.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detailed Description */}
          <div>
            <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
              详细说明 <span className="text-foreground-400 font-normal">(选填)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, 200))}
              rows={3}
              placeholder="请输入补充说明，帮助审核人员更快定位问题..."
              className="w-full text-xs p-3 rounded-xl border border-background-200 focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all placeholder:text-foreground-300 resize-none"
            />
            <div className="text-right text-[11px] text-foreground-400 mt-1">
              {description.length}/200
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 rounded-xl text-xs font-medium text-foreground-600 hover:bg-gray-100 transition-all cursor-pointer"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 active:bg-rose-800 text-white text-xs font-medium transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              {submitting && <i className="ri-loader-4-line animate-spin text-sm"></i>}
              <span>{submitting ? '提交中...' : '提交举报'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
